/**
 * WritingTab — shared implementation for the Writing / Prompts / Critiques
 * tabs in writer communities, filtered by post type.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { fadeUp, stagger } from '@/lib/motion';
import { useCommunities } from '../context/CommunitiesContext';
import { useModalState } from '../hooks';
import PostCard from '../post/PostCard';
import PostComposer from '../post/PostComposer';
import EmptyState from '../EmptyState';

const MODE_CONFIG = {
  writing: {
    types: ['writing_update', 'share_writing'],
    defaultType: 'share_writing',
    cta: 'Share your work…',
    heading: "It's quiet in here… start the conversation.",
  },
  prompts: {
    types: ['writing_prompt'],
    defaultType: 'writing_prompt',
    cta: 'Post a writing prompt…',
  },
  critiques: {
    types: ['feedback_request'],
    defaultType: 'feedback_request',
    cta: 'Ask for feedback on your work…',
  },
};

export default function WritingTab({ community, role, mode }) {
  const { getPosts } = useCommunities();
  const [isComposerOpen, openComposer, closeComposer] = useModalState(false);
  const config = MODE_CONFIG[mode];
  const posts = getPosts(community.id)
    .filter(p => config.types.includes(p.type))
    .sort((a, b) => b.createdAt - a.createdAt);

  return (
    <div className="u-container max-w-2xl py-8">
      <button
        type="button" onClick={openComposer}
        className="flex w-full items-center gap-3 rounded-full border border-line bg-card px-4 py-3 text-left text-sm text-subtle shadow-soft transition-colors hover:border-coral/40"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-coral/10 text-coral"><Plus className="h-4 w-4" /></span>
        {config.cta}
      </button>

      {posts.length === 0 ? (
        <EmptyState type="discussions" onCTAClick={openComposer} />
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="mt-6 space-y-4">
          {posts.map((post, i) => (
            <motion.div key={post.id} variants={fadeUp} custom={i % 8}>
              <PostCard post={post} communityRole={role} />
            </motion.div>
          ))}
        </motion.div>
      )}

      <PostComposer isOpen={isComposerOpen} onClose={closeComposer} community={community} defaultType={config.defaultType} />
    </div>
  );
}
